import { Alert, Stack, TextField } from "@mui/material"
import type { BiomeshOperationResponse } from "@phc/common/gen/threatAnalyticsGen"
import {
  getListOperationsBiomeshOperationListGetQueryKey,
  useCreateOperationBiomeshOperationPost,
  useListContactsBiomeshContactListGet,
  useListPlatformsBiomeshPlatformListGet,
  useListUnitsBiomeshUnitListGet,
  useUpdateOperationBiomeshOperationOperationIdPut,
} from "@phc/common/gen/threatAnalyticsGen"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import type { FC } from "react"
import { useCallback, useState } from "react"
import { Modal } from "../Shared/Modal"
import { ContactFormDialog } from "./ContactFormDialog"
import { extractApiError } from "./copUtils"
import { MultiAutocomplete } from "./MultiAutocomplete"

interface OperationFormDialogProps {
  open: boolean
  onClose: () => void
  operation?: BiomeshOperationResponse
  onSaved?: (operationId: string) => void
}

export const OperationFormDialog: FC<OperationFormDialogProps> = ({
  open,
  onClose,
  operation,
  onSaved,
}) => {
  const isEdit = !!operation
  const queryClient = useQueryClient()

  const [name, setName] = useState(operation?.name ?? "")
  const [unitIds, setUnitIds] = useState<string[]>(
    () => operation?.units?.flatMap(u => (u.unit_id ? [u.unit_id] : [])) ?? []
  )
  const [nodeIds, setNodeIds] = useState<string[]>(
    () =>
      operation?.platforms?.flatMap(p =>
        p.platform_id ? [p.platform_id] : []
      ) ?? []
  )
  const [contactIds, setContactIds] = useState<string[]>(
    () =>
      operation?.contacts?.flatMap(c => (c.contact_id ? [c.contact_id] : [])) ??
      []
  )
  const [formError, setFormError] = useState("")
  const [contactDialogOpen, setContactDialogOpen] = useState(false)

  const { data: units } = useListUnitsBiomeshUnitListGet()
  const { data: nodes } = useListPlatformsBiomeshPlatformListGet()
  const { data: contacts } = useListContactsBiomeshContactListGet()

  const { mutateAsync: createOperation, isPending: createPending } =
    useCreateOperationBiomeshOperationPost()
  const { mutateAsync: updateOperation, isPending: updatePending } =
    useUpdateOperationBiomeshOperationOperationIdPut()

  const isPending = createPending || updatePending

  const handleSave = useCallback(async () => {
    setFormError("")

    if (!name.trim()) {
      setFormError("Name is required")
      return
    }

    const payload = {
      name: name.trim(),
      unit_ids: unitIds,
      platform_ids: nodeIds,
      contact_ids: contactIds,
    }

    try {
      if (operation) {
        await updateOperation({
          operationId: operation.operation_id,
          data: payload,
        })
        enqueueSnackbar("Operation updated", { variant: "success" })
        onSaved?.(operation.operation_id)
      } else {
        const result = await createOperation({ data: payload })
        enqueueSnackbar("Operation created", { variant: "success" })
        onSaved?.(result.operation_id)
      }
      await queryClient.invalidateQueries({
        queryKey: getListOperationsBiomeshOperationListGetQueryKey(),
      })
      onClose()
    } catch (err) {
      setFormError(extractApiError(err, "Failed to save operation"))
    }
  }, [
    name,
    unitIds,
    nodeIds,
    contactIds,
    operation,
    createOperation,
    updateOperation,
    queryClient,
    onClose,
    onSaved,
  ])

  const unitOptions = units?.map(u => ({ id: u.unit_id, label: u.name })) ?? []
  const nodeOptions =
    nodes?.map(p => ({ id: p.platform_id, label: p.name })) ?? []
  const contactOptions =
    contacts?.map(c => ({
      id: c.contact_id,
      label: `${c.rank ? `${c.rank} ` : ""}${c.first_name} ${c.last_name}`,
    })) ?? []

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        onConfirm={() => void handleSave()}
        title={isEdit ? "Edit Operation" : "New Operation"}
        confirmLabel="Save"
        disableBtns={isPending}
      >
        <Stack spacing={1.5}>
          <TextField
            label="Name"
            required
            fullWidth
            value={name}
            onChange={e => setName(e.target.value)}
          />

          <MultiAutocomplete
            label="Units"
            options={unitOptions}
            selectedIds={unitIds}
            onChange={setUnitIds}
          />

          <MultiAutocomplete
            label="Nodes"
            options={nodeOptions}
            selectedIds={nodeIds}
            onChange={setNodeIds}
          />

          <MultiAutocomplete
            label="Contacts"
            options={contactOptions}
            selectedIds={contactIds}
            onChange={setContactIds}
            onCreateNew={() => setContactDialogOpen(true)}
          />

          {formError && <Alert severity="error">{formError}</Alert>}
        </Stack>
      </Modal>

      <ContactFormDialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        onSaved={id => setContactIds(prev => [...prev, id])}
      />
    </>
  )
}
